import Vue from 'vue';
import { client } from '@/services';
import { getSelectedVenue } from '@/services/helpers';
import { PLAYLIST_STATUS } from '@/constants';

const schedulesService = client.service('schedules');
const playlistsService = client.service('playlists');
const assetsService = client.service('assets');
const daySorter = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6
};

const schedules = {
  namespaced: true,
  state: {
    schedules: [],
    loaded: false
  },
  getters: {
    GET_SCHEDULES: state => {
      return state.schedules.map(item => {
        if (item.days.length === 7) {
          item.fullSchedule = `Every day, ${item.startTime} to ${item.endTime}`;
        } else {
          const days = item.days.sort((a, b) => daySorter[a] - daySorter[b]);
          item.fullSchedule = `${days.join(',')}, ${item.startTime} to ${item.endTime}`;
        }

        if (item.priority && item.priority.length && !isNaN(item.priority)) {
          item.priority = parseInt(item.priority);
        }

        return item;
      });
    },
    GET_LOADED_STATUS: state => state.loaded
  },
  mutations: {
    SET_SCHEDULES(state, schedules) {
      state.schedules = schedules;
      state.loaded = true;
    },
    ADD_SCHEDULE(state, schedule) {
      state.schedules.push(schedule);
    },
    UPDATE_SCHEDULE(state, updatedSchedule) {
      const index = state.schedules.findIndex(schedule => schedule._id === updatedSchedule._id);

      if (index >= 0) {
        Vue.set(state.schedules, index, updatedSchedule);
      }
    },
    REMOVE_SCHEDULE(state, _id) {
      const i = state.schedules.findIndex(sched => sched._id === _id);
      if (i >= 0) {
        state.schedules.splice(i, 1);
      }
    }
  },
  actions: {
    async GET_SCHEDULES({ commit }) {
      const schedules = await schedulesService.find({
        query: {
          venueId: getSelectedVenue()._id,
          $paginate: {
            value: false
          },
          $with_playlist_and_group: {
            value: true
          }
        }
      });

      for (const schedule of schedules) {
        if (schedule.hasOwnProperty('sourceType')) {
          if (schedule.sourceType === 'Playlist') {
            schedule.sourceTypeValue = schedule.playlist.name;
          } else if (schedule.sourceType === 'AdBox Video') {
            schedule.sourceTypeValue = schedule.assetId;
          } else if (schedule.sourceType === 'IPTV') {
            schedule.sourceTypeValue = schedule.url;
          } else if (schedule.sourceType === 'App') {
            try {
              schedule.sourceTypeValue = (await assetsService.get(schedule.appId)).name;
            } catch (e) {
              schedule.sourceTypeValue = 'App deleted.';
            }
          }
        } else {
          schedule.sourceType = 'Playlist';
          schedule.sourceTypeValue = schedule.playlist.name;
        }
      }

      commit('SET_SCHEDULES', schedules);
      return schedules;
    },
    async GET_SCHEDULES_COUNT_BY_NAME(_, scheduleName) {
      const schedules = await schedulesService.find({
        query: {
          venueId: getSelectedVenue()._id,
          $paginate: {
            value: false
          },
          $with_playlist_and_group: {
            value: true
          }
        }
      });

      return schedules.filter(schedule =>
        schedule.name.toLowerCase().includes(scheduleName.toLowerCase())
      ).length;
    },
    ADD_SCHEDULE({ rootState }, payload) {
      const schedule = JSON.parse(JSON.stringify(payload));

      if (schedule.priority && schedule.priority.length && !isNaN(schedule.priority)) {
        schedule.priority = parseInt(schedule.priority);
      }

      schedule.status = PLAYLIST_STATUS.DRAFT;
      schedule.created_at = Date.now();
      schedule.userId = rootState.users.user._id;
      schedule.venueId = getSelectedVenue()._id;
      return schedulesService.create(schedule);
    },
    async ADD_SCHEDULE_DUPLICATE({ rootState, dispatch }, payload) {
      try {
        // get count of duplicate Schedule
        const sameNameCount = await dispatch('GET_SCHEDULES_COUNT_BY_NAME', payload.name);

        delete payload._id;
        payload.name = `Copy of ${payload.name} - ${sameNameCount}`;

        const clonedSchedule = await schedulesService.create(payload);

        return clonedSchedule;
      } catch (error) {
        throw error;
      }
    },

    async UPDATE_SCHEDULE({ commit }, payload) {
      try {
        //clone schedule
        const schedule = JSON.parse(JSON.stringify(payload.schedule));

        if (schedule.priority && schedule.priority.length && !isNaN(schedule.priority)) {
          schedule.priority = parseInt(schedule.priority);
        }

        schedule.venueId = getSelectedVenue()._id;
        schedule.updated_at = Date.now();
        schedule.status = payload.status;

        const updated = await schedulesService.update(schedule._id, schedule);
        commit('UPDATE_SCHEDULE', updated);
        return updated;
      } catch (error) {
        throw error;
      }
    },
    async UPDATE_SCHEDULE_STATUS({ commit }, payload) {
      try {
        //clone schedule
        const schedule = JSON.parse(JSON.stringify(payload.schedule));

        schedule.updated_at = Date.now();
        schedule.status = payload.status;

        const patched = await schedulesService.patch(schedule._id, schedule);

        commit('UPDATE_SCHEDULE', patched);
        return patched;
      } catch (error) {
        throw error;
      }
    },
    UPDATE_SCHEDULE_TAKEOVER({ commit }, payload) {
      try {
        //clone schedule
        const schedule = JSON.parse(JSON.stringify(payload.schedule));

        schedule.updated_at = Date.now();
        schedule.takeover = payload.takeover;

        commit('UPDATE_SCHEDULE', schedule);

        return schedulesService.patch(schedule._id, {
          takeover: payload.takeover,
          updated_at: schedule.updated_at
        });
      } catch (error) {
        throw error;
      }
    },
    UPDATE_SCHEDULE_TAKEOVER({ commit }, payload) {
      try {
        //clone schedule
        const schedule = JSON.parse(JSON.stringify(payload.schedule));

        schedule.updated_at = Date.now();
        schedule.takeover = payload.takeover;

        commit('UPDATE_SCHEDULE', schedule);

        return schedulesService.patch(schedule._id, {
          takeover: payload.takeover,
          updated_at: schedule.updated_at
        });
      } catch (error) {
        throw error;
      }
    },

    async DELETE_SCHEDULES({ commit }, schedules) {
      for (const schedule of schedules) {
        await schedulesService.remove(schedule._id);
        commit('REMOVE_SCHEDULE', schedule._id);
      }
      return { success: true };
    },
    async GET_ONE_SCHEDULE(_, id) {
      const response = await schedulesService.get(id);

      try {
        response.startDate = response.startDate;
        response.endDate = response.endDate;
        response.playlist = await playlistsService.get(response.playlist);
      } catch (e) {
        response.playlist = null;
      }

      return response;
    },
    async SEARCH_PLAYLIST(_, _id) {
      try {
        return (
          await schedulesService.find({
            query: {
              playlist: _id
            }
          })
        ).data;
      } catch (e) {
        throw e;
      }
    },
    async SEARCH_DEVICE_GROUP(_, _id) {
      try {
        return (
          await schedulesService.find({
            query: {
              group: _id
            }
          })
        ).data;
      } catch (e) {
        throw e;
      }
    },
    async SEARCH_APP_ASSET(_, _id) {
      return (
        await schedulesService.find({
          query: {
            appId: _id
          }
        })
      ).data;
    }
  }
};

export default schedules;



// WEBPACK FOOTER //
// ./src/store/modules/schedules.js